import React, { useState } from 'react';
import SEO from '../components/SEO';
import { useT } from '../i18n/LanguageContext';

const eyebrowStyle = {
  fontFamily: 'Inter, sans-serif',
  fontSize: '9pt',
  fontWeight: 600,
  letterSpacing: '0.32em',
  textTransform: 'uppercase',
  color: '#D6A87A',
};

const fieldLabelStyle = {
  fontFamily: 'Inter, sans-serif',
  fontSize: '10pt',
  fontWeight: 600,
  letterSpacing: '0.22em',
  textTransform: 'uppercase',
  color: '#D6A87A',
  marginBottom: '8px',
  display: 'block',
};

const inputBase = {
  fontFamily: 'Inter, sans-serif',
  fontSize: '14px',
  fontWeight: 400,
  color: '#0A132F',
  backgroundColor: '#FFFFFF',
  border: '1px solid rgba(214,168,122,0.30)',
  borderRadius: '0',
  height: '48px',
  width: '100%',
  padding: '0 16px',
  outline: 'none',
  transition: 'border-color 0.25s ease',
};

const Field = ({ label, children, fullWidth = false }) => (
  <div className={fullWidth ? 'md:col-span-2' : ''}>
    <label style={fieldLabelStyle}>{label}</label>
    {children}
  </div>
);

const Input = (props) => (
  <input
    {...props}
    style={{ ...inputBase, ...(props.style || {}) }}
    onFocus={(e) => {
      e.currentTarget.style.borderColor = '#D6A87A';
      props.onFocus && props.onFocus(e);
    }}
    onBlur={(e) => {
      e.currentTarget.style.borderColor = 'rgba(214,168,122,0.30)';
      props.onBlur && props.onBlur(e);
    }}
  />
);

const Select = ({ name, value, onChange, options, required, placeholder }) => (
  <div className="relative">
    <select
      name={name}
      value={value}
      onChange={onChange}
      required={required}
      style={{
        ...inputBase,
        appearance: 'none',
        WebkitAppearance: 'none',
        MozAppearance: 'none',
        paddingInlineEnd: '40px',
      }}
      onFocus={(e) => {
        e.currentTarget.style.borderColor = '#D6A87A';
      }}
      onBlur={(e) => {
        e.currentTarget.style.borderColor = 'rgba(214,168,122,0.30)';
      }}
    >
      <option value="" disabled>
        {placeholder}
      </option>
      {options.map((opt) => (
        <option key={opt.value} value={opt.value}>
          {opt.label}
        </option>
      ))}
    </select>
    {/* copper chevron, logical inset so it mirrors in RTL */}
    <span
      aria-hidden="true"
      className="pointer-events-none absolute top-1/2 -translate-y-1/2"
      style={{ color: '#D6A87A', insetInlineEnd: '16px' }}
    >
      <svg width="12" height="8" viewBox="0 0 12 8" fill="none">
        <path
          d="M1 1.5L6 6.5L11 1.5"
          stroke="currentColor"
          strokeWidth="1.5"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
      </svg>
    </span>
  </div>
);

const Textarea = (props) => (
  <textarea
    {...props}
    style={{
      ...inputBase,
      height: 'auto',
      padding: '14px 16px',
      lineHeight: 1.6,
      resize: 'vertical',
      ...(props.style || {}),
    }}
    onFocus={(e) => {
      e.currentTarget.style.borderColor = '#D6A87A';
    }}
    onBlur={(e) => {
      e.currentTarget.style.borderColor = 'rgba(214,168,122,0.30)';
    }}
  />
);

const SocialIcon = ({ kind }) => {
  const stroke = '#D6A87A';
  if (kind === 'linkedin') {
    return (
      <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
        <rect
          x="3"
          y="3"
          width="18"
          height="18"
          rx="2"
          stroke={stroke}
          strokeWidth="1.5"
        />
        <path
          d="M8 10v7M8 7v.01M12 17v-4a2 2 0 0 1 4 0v4M12 13v4"
          stroke={stroke}
          strokeWidth="1.5"
          strokeLinecap="round"
        />
      </svg>
    );
  }
  if (kind === 'instagram') {
    return (
      <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
        <rect
          x="3"
          y="3"
          width="18"
          height="18"
          rx="5"
          stroke={stroke}
          strokeWidth="1.5"
        />
        <circle cx="12" cy="12" r="3.5" stroke={stroke} strokeWidth="1.5" />
        <circle cx="17.5" cy="6.5" r="0.8" fill={stroke} />
      </svg>
    );
  }
  // whatsapp
  return (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
      <path
        d="M3 21l1.65-4.5A8.5 8.5 0 1 1 8 19.85L3 21z"
        stroke={stroke}
        strokeWidth="1.5"
        strokeLinejoin="round"
      />
      <path
        d="M9 9.5c0-.5.4-1 1-1h.5c.4 0 .7.2.8.6l.4 1.4-.6.5a5 5 0 0 0 2.4 2.4l.5-.6 1.4.4c.4.1.6.4.6.8v.5c0 .6-.5 1-1 1A6 6 0 0 1 9 9.5z"
        stroke={stroke}
        strokeWidth="1.5"
        strokeLinejoin="round"
      />
    </svg>
  );
};

const Contact = () => {
  const t = useT();
  const eventTypeOptions = t('contact.form.options.eventTypes') || [];
  const budgetOptions = t('contact.form.options.budgets') || [];
  const [form, setForm] = useState({
    name: '',
    company: '',
    email: '',
    role: '',
    country: '',
    eventType: '',
    guestCount: '',
    dateWindow: '',
    budget: '',
    message: '',
    consent: false,
  });
  const [status, setStatus] = useState('idle'); // idle | sending | success | error
  const [errorMsg, setErrorMsg] = useState('');

  const onChange = (e) => {
    const { name, value, type, checked } = e.target;
    setForm((f) => ({ ...f, [name]: type === 'checkbox' ? checked : value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.consent) return;
    setStatus('sending');
    setErrorMsg('');
    try {
      const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
      const response = await fetch(`${BACKEND_URL}/api/contact-inquiry`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          name: form.name,
          email: form.email,
          phone: '',
          company: [form.company, form.role, form.country].filter(Boolean).join(' · '),
          message: [
            `Event Type: ${form.eventType || ', '}`,
            `Guest Count: ${form.guestCount || ', '}`,
            `Date Window: ${form.dateWindow || ', '}`,
            `Budget: ${form.budget || ', '}`,
            '',
            'Brief:',
            form.message,
          ].join('\n'),
        }),
      });
      if (!response.ok) throw new Error('submit_failed');
      setStatus('success');
    } catch (err) {
      setStatus('error');
      setErrorMsg(t('contact.form.error'));
    }
  };

  return (
    <div data-testid="contact-page">
      <SEO
        title="Contact GlowMeets, Tell Us About Your Room"
        description="Begin the conversation with GlowMeets. We answer briefs in a single working day, in your language. Istanbul · Dubai · Delaware."
        keywords="contact GlowMeets, event consultation Turkey, MICE inquiry Istanbul, destination wedding inquiry"
        url="/contact"
      />

      {/* ───────────────────────── 1. CONTACT HERO ───────────────────────── */}
      <section
        className="bg-navy w-full flex items-center justify-center"
        style={{ minHeight: '100vh', paddingTop: '120px', paddingBottom: '90px' }}
        data-testid="contact-hero"
      >
        <div className="max-w-container mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <div style={eyebrowStyle} className="mb-7">
            {t('contact.hero.eyebrow')}
          </div>

          <h1
            className="text-bone mx-auto"
            style={{
              fontFamily: '"Cormorant Garamond", serif',
              fontSize: 'clamp(40px, 5vw, 64pt)',
              lineHeight: 1.05,
              fontWeight: 500,
              letterSpacing: '-0.005em',
              maxWidth: '18ch',
            }}
          >
            <span className="block">{t('contact.hero.line1')}</span>
            <span
              className="block italic"
              style={{ color: '#D6A87A', fontWeight: 400 }}
            >
              {t('contact.hero.line2')}
            </span>
          </h1>

          <p
            className="mt-7 mx-auto"
            style={{
              fontFamily: '"Cormorant Garamond", serif',
              fontStyle: 'italic',
              fontWeight: 400,
              fontSize: '18pt',
              color: '#D6A87A',
              lineHeight: 1.5,
              maxWidth: '50ch',
            }}
          >
            {t('contact.hero.sub')}
          </p>

          {/* 60px vertical copper hairline */}
          <div className="mt-12 flex justify-center">
            <span
              style={{
                display: 'block',
                width: '1px',
                height: '60px',
                backgroundColor: 'rgba(214,168,122,0.6)',
              }}
            />
          </div>
        </div>
      </section>

      {/* ───────────────────────── 2. FORM SECTION ───────────────────────── */}
      <section
        className="bg-bone"
        style={{ paddingTop: '120px', paddingBottom: '120px' }}
        data-testid="contact-form-section"
      >
        <div className="max-w-[720px] mx-auto px-4 sm:px-6 lg:px-8">
          {status !== 'success' && (
            <>
              <div className="text-center mb-12">
                <div style={eyebrowStyle} className="mb-5">
                  {t('contact.form.eyebrow')}
                </div>
                <h2
                  style={{
                    fontFamily: '"Cormorant Garamond", serif',
                    fontSize: 'clamp(28px, 2.8vw, 36pt)',
                    lineHeight: 1.15,
                    fontWeight: 500,
                    color: '#0A132F',
                    letterSpacing: '-0.005em',
                  }}
                >
                  {t('contact.form.headline1')}{' '}
                  <em
                    className="italic"
                    style={{ color: '#B98556', fontWeight: 400 }}
                  >
                    {t('contact.form.headline2')}
                  </em>
                </h2>
                <p
                  className="mt-5 mx-auto"
                  style={{
                    fontFamily: '"Cormorant Garamond", serif',
                    fontStyle: 'italic',
                    fontWeight: 400,
                    fontSize: '14pt',
                    color: '#4A4F63',
                    lineHeight: 1.55,
                    maxWidth: '56ch',
                  }}
                >
                  {t('contact.form.sub')}
                </p>
              </div>

              <form onSubmit={handleSubmit} className="space-y-6" data-testid="contact-form">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <Field label={t('contact.form.fields.name')}>
                    <Input
                      type="text"
                      name="name"
                      value={form.name}
                      onChange={onChange}
                      required
                      data-testid="field-name"
                    />
                  </Field>
                  <Field label={t('contact.form.fields.company')}>
                    <Input
                      type="text"
                      name="company"
                      value={form.company}
                      onChange={onChange}
                      data-testid="field-company"
                    />
                  </Field>

                  <Field label={t('contact.form.fields.email')}>
                    <Input
                      type="email"
                      name="email"
                      value={form.email}
                      onChange={onChange}
                      required
                      data-testid="field-email"
                    />
                  </Field>
                  <Field label={t('contact.form.fields.role')}>
                    <Input
                      type="text"
                      name="role"
                      value={form.role}
                      onChange={onChange}
                      data-testid="field-role"
                    />
                  </Field>

                  <Field label={t('contact.form.fields.country')}>
                    <Input
                      type="text"
                      name="country"
                      value={form.country}
                      onChange={onChange}
                      data-testid="field-country"
                    />
                  </Field>
                  <Field label={t('contact.form.fields.eventType')}>
                    <Select
                      name="eventType"
                      value={form.eventType}
                      onChange={onChange}
                      options={eventTypeOptions}
                      placeholder={t('contact.form.placeholders.eventType')}
                      required
                    />
                  </Field>

                  <Field label={t('contact.form.fields.guestCount')}>
                    <Input
                      type="text"
                      name="guestCount"
                      value={form.guestCount}
                      onChange={onChange}
                      placeholder={t('contact.form.placeholders.guestCount')}
                      data-testid="field-guests"
                    />
                  </Field>
                  <Field label={t('contact.form.fields.dateWindow')}>
                    <Input
                      type="text"
                      name="dateWindow"
                      value={form.dateWindow}
                      onChange={onChange}
                      placeholder={t('contact.form.placeholders.dateWindow')}
                      data-testid="field-date"
                    />
                  </Field>

                  <Field label={t('contact.form.fields.budget')} fullWidth>
                    <Select
                      name="budget"
                      value={form.budget}
                      onChange={onChange}
                      options={budgetOptions}
                      placeholder={t('contact.form.placeholders.budget')}
                    />
                  </Field>

                  <Field label={t('contact.form.fields.message')} fullWidth>
                    <Textarea
                      name="message"
                      value={form.message}
                      onChange={onChange}
                      rows={6}
                      required
                      data-testid="field-message"
                      placeholder={t('contact.form.placeholders.message')}
                    />
                  </Field>
                </div>

                {/* consent */}
                <label
                  className="flex items-start gap-3 select-none cursor-pointer"
                  style={{
                    fontFamily: 'Inter, sans-serif',
                    fontSize: '10pt',
                    color: '#4A4F63',
                    lineHeight: 1.55,
                  }}
                >
                  <input
                    type="checkbox"
                    name="consent"
                    checked={form.consent}
                    onChange={onChange}
                    required
                    data-testid="field-consent"
                    style={{
                      marginTop: '3px',
                      width: '14px',
                      height: '14px',
                      accentColor: '#D6A87A',
                      flexShrink: 0,
                    }}
                  />
                  <span>
                    {t('contact.form.consent')}
                  </span>
                </label>

                {status === 'error' && (
                  <p
                    style={{
                      fontFamily: 'Inter, sans-serif',
                      fontSize: '11pt',
                      color: '#B98556',
                      textAlign: 'center',
                    }}
                  >
                    {errorMsg}
                  </p>
                )}

                {/* Submit */}
                <div className="pt-4 flex flex-col items-center gap-4">
                  <button
                    type="submit"
                    disabled={status === 'sending' || !form.consent}
                    data-testid="contact-submit"
                    style={{
                      fontFamily: 'Inter, sans-serif',
                      fontSize: '14pt',
                      fontWeight: 600,
                      letterSpacing: '0.18em',
                      textTransform: 'uppercase',
                      color: '#0A132F',
                      backgroundColor: '#D6A87A',
                      border: '1px solid #D6A87A',
                      borderRadius: '999px',
                      minWidth: '320px',
                      height: '60px',
                      padding: '0 28px',
                      cursor:
                        status === 'sending' || !form.consent
                          ? 'not-allowed'
                          : 'pointer',
                      opacity: !form.consent ? 0.55 : 1,
                      transition: 'background-color 0.25s ease, color 0.25s ease',
                    }}
                    onMouseEnter={(e) => {
                      if (status === 'sending' || !form.consent) return;
                      e.currentTarget.style.backgroundColor = '#B98556';
                      e.currentTarget.style.borderColor = '#B98556';
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.backgroundColor = '#D6A87A';
                      e.currentTarget.style.borderColor = '#D6A87A';
                    }}
                  >
                    {status === 'sending' ? t('cta.sending') : t('cta.bookConsultation')}
                  </button>

                  {status === 'sending' && (
                    <div
                      aria-hidden="true"
                      style={{
                        width: '120px',
                        height: '1px',
                        backgroundColor: 'rgba(214,168,122,0.35)',
                        position: 'relative',
                        overflow: 'hidden',
                      }}
                    >
                      <span
                        style={{
                          position: 'absolute',
                          left: 0,
                          top: 0,
                          height: '1px',
                          width: '40%',
                          backgroundColor: '#D6A87A',
                          animation: 'copperProgress 1.2s linear infinite',
                        }}
                      />
                    </div>
                  )}
                </div>
              </form>
            </>
          )}

          {status === 'success' && (
            <div className="text-center" data-testid="contact-success">
              <p
                style={{
                  fontFamily: '"Cormorant Garamond", serif',
                  fontStyle: 'italic',
                  fontWeight: 400,
                  fontSize: '28pt',
                  lineHeight: 1.25,
                  color: '#0A132F',
                  letterSpacing: '-0.005em',
                  maxWidth: '24ch',
                  margin: '0 auto',
                }}
              >
                {t('contact.form.success')}
              </p>
              <div
                className="mt-10 mx-auto"
                style={{
                  height: '1px',
                  width: '120px',
                  backgroundColor: 'rgba(214,168,122,0.55)',
                }}
              />
            </div>
          )}
        </div>

        {/* keyframe for sending bar */}
        <style>{`
          @keyframes copperProgress {
            0%   { transform: translateX(-100%); }
            100% { transform: translateX(300%); }
          }
        `}</style>
      </section>

      {/* ───────────────────────── 3. DIRECT CONTACT STRIP ───────────────────────── */}
      <section
        className="bg-navy text-bone w-full"
        style={{ paddingTop: '80px', paddingBottom: '80px' }}
        data-testid="contact-direct"
      >
        <div className="max-w-container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-y-10 md:gap-y-0">
            {/* DIRECT */}
            <div
              className="text-center md:px-8"
              style={{ borderRight: '1px solid rgba(214,168,122,0.35)' }}
              data-testid="direct-col-direct"
            >
              <div
                style={{
                  fontFamily: 'Inter, sans-serif',
                  fontSize: '8pt',
                  fontWeight: 600,
                  letterSpacing: '0.32em',
                  textTransform: 'uppercase',
                  color: '#D6A87A',
                }}
                className="mb-6"
              >
                {t('contact.direct.direct')}
              </div>
              <a
                href="mailto:mice@glowmeets.com"
                style={{
                  fontFamily: '"Cormorant Garamond", serif',
                  fontSize: '20pt',
                  fontWeight: 500,
                  color: '#D6A87A',
                  letterSpacing: '0.005em',
                }}
                className="block hover:opacity-80 transition-opacity"
              >
                mice@glowmeets.com
              </a>
              <a
                href="https://wa.me/905441181827"
                target="_blank"
                rel="noopener noreferrer"
                className="block mt-3 hover:opacity-80 transition-opacity"
                style={{
                  fontFamily: 'Inter, sans-serif',
                  fontSize: '11pt',
                  letterSpacing: '0.06em',
                  color: '#D6A87A',
                }}
              >
                +90 544 118 18 27, {t('contact.direct.whatsappLabel')}
              </a>
            </div>

            {/* SOCIAL */}
            <div
              className="text-center md:px-8"
              style={{ borderRight: '1px solid rgba(214,168,122,0.35)' }}
              data-testid="direct-col-social"
            >
              <div
                style={{
                  fontFamily: 'Inter, sans-serif',
                  fontSize: '8pt',
                  fontWeight: 600,
                  letterSpacing: '0.32em',
                  textTransform: 'uppercase',
                  color: '#D6A87A',
                }}
                className="mb-6"
              >
                {t('contact.direct.social')}
              </div>
              <div className="flex items-center justify-center gap-6">
                <a
                  href="https://linkedin.com/company/glowmeets"
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label="LinkedIn"
                  className="hover:opacity-70 transition-opacity"
                >
                  <SocialIcon kind="linkedin" />
                </a>
                <a
                  href="https://instagram.com/glowmeets"
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label="Instagram"
                  className="hover:opacity-70 transition-opacity"
                >
                  <SocialIcon kind="instagram" />
                </a>
                <a
                  href="https://wa.me/905441181827"
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label="WhatsApp"
                  className="hover:opacity-70 transition-opacity"
                >
                  <SocialIcon kind="whatsapp" />
                </a>
              </div>
            </div>

            {/* OFFICES */}
            <div
              className="text-center md:px-8"
              data-testid="direct-col-offices"
            >
              <div
                style={{
                  fontFamily: 'Inter, sans-serif',
                  fontSize: '8pt',
                  fontWeight: 600,
                  letterSpacing: '0.32em',
                  textTransform: 'uppercase',
                  color: '#D6A87A',
                }}
                className="mb-6"
              >
                {t('contact.direct.offices')}
              </div>

              <div
                style={{
                  fontFamily: 'Inter, sans-serif',
                  fontSize: '10pt',
                  fontWeight: 600,
                  letterSpacing: '0.28em',
                  textTransform: 'uppercase',
                  color: '#D6A87A',
                }}
                className="mb-3"
              >
                İstanbul · Dubai · Delaware
              </div>

              <div
                className="space-y-2"
                style={{
                  fontFamily: 'Inter, sans-serif',
                  fontSize: '9pt',
                  color: 'rgba(244,239,227,0.6)',
                  lineHeight: 1.6,
                }}
              >
                <p>Premier Kampüs Ofis, Kağıthane, Istanbul</p>
                <p>OneJLT, Jumeirah Lakes Towers, Dubai</p>
                <p>1111B South Governors Avenue, Dover DE 19904</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ───────────────────────── 4. CLOSING BAND ───────────────────────── */}
      <section
        className="relative w-full"
        style={{
          paddingTop: '100px',
          paddingBottom: '100px',
          background:
            'linear-gradient(180deg, #0A132F 0%, #0E1B3F 50%, #060B22 100%)',
        }}
        data-testid="contact-closing"
      >
        <div className="max-w-container mx-auto px-4 sm:px-6 lg:px-8 text-center">
          {/* Compass mark */}
          <div className="flex justify-center">
            <img
              src="/glowmeets-mark.png"
              alt="GlowMeets compass mark"
              style={{ height: '64px', width: 'auto' }}
              data-testid="closing-compass"
            />
          </div>

          <p
            className="mt-8 mx-auto"
            style={{
              fontFamily: '"Cormorant Garamond", serif',
              fontStyle: 'italic',
              fontWeight: 400,
              fontSize: '14pt',
              color: 'rgba(244,239,227,0.7)',
              lineHeight: 1.5,
              letterSpacing: '0.005em',
            }}
          >
            GlowMeets · A Mervin Travel brand · Tursab Licensed No. 7790 ·
            Istanbul, Türkiye
          </p>

          <p
            className="mt-4"
            style={{
              fontFamily: 'Inter, sans-serif',
              fontSize: '8pt',
              color: 'rgba(214,168,122,0.6)',
              letterSpacing: '0.18em',
              textTransform: 'uppercase',
            }}
          >
            © {new Date().getFullYear()} GlowMeets. {t('footer.rights')}
          </p>
        </div>
      </section>
    </div>
  );
};

export default Contact;
